import { Tabs, Tab, Box, CircularProgress } from '@mui/material'
import { t } from '@lingui/macro'
import dayjs from 'dayjs'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import {
  useAccordionState,
  useDateManagement,
  useLoadingStates,
  useDurationMetrics,
  useStatisticsData,
  useTabsState,
} from '@/features/statistics'
import { useEntityEvolution } from '../model/useEntityEvolution'
import { TabPanel } from './TabPanel'
import { OverviewSection } from './OverviewSection'
import { DurationSection } from './DurationSection'
import { AIUsageSection } from './AIUsageSection'
import { MedicalActsSection } from './MedicalActsSection'
import { EntityEvolutionSection } from './EntityEvolutionSection'
import { DailySection } from './DailySection'
import { WeeklySection } from './WeeklySection'
import { MonthlyReportTab } from './monthly-report/MonthlyReportTab'

export type StatsContainerProps = {
  establishmentId: string
  isSuperAdmin?: boolean
}

export function StatsContainer(props: StatsContainerProps) {
  const { establishmentId, isSuperAdmin = false } = props
  const isMobile = useIsMobile()

  // Custom hooks
  const { activeTab, handleTabChange } = useTabsState(0)
  const { expandedSection, handleAccordionChange } = useAccordionState('overview')
  const loadingStates = useLoadingStates()
  const { selectedDate, selectedRange, handleDateChange, handleRangeChange } = useDateManagement()

  const durationMetrics = useDurationMetrics(establishmentId, selectedRange, expandedSection)
  const statisticsData = useStatisticsData(
    establishmentId,
    selectedDate,
    selectedRange,
    expandedSection,
    loadingStates
  )

  const dateChangeHandler = handleDateChange(expandedSection, loadingStates.resetDailyLoaded)
  const rangeChangeHandler = handleRangeChange(
    expandedSection,
    loadingStates.resetWeeklyLoaded,
    loadingStates.resetDurationMetricsLoaded
  )

  const { data: entityEvolutionData, isLoading: isLoadingEntityEvolution } = useEntityEvolution({
    establishmentId,
    dateRange: selectedRange,
    isExpanded: expandedSection === 'entityEvolution',
  })

  if (!selectedDate) {
    return <CircularProgress />
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          '& .MuiTab-root': {
            fontSize: isMobile ? '0.8rem' : 'inherit',
            minWidth: isMobile ? 'auto' : 90,
            px: isMobile ? 1 : 2,
          },
        }}
      >
        <Tab label={t`Summary`} />
        <Tab label={t`Detailed`} />
        <Tab label={t`Monthly Report`} />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <OverviewSection
          isExpanded={expandedSection === 'overview'}
          onChange={handleAccordionChange('overview')}
          establishmentId={establishmentId}
          isMobile={isMobile}
        />

        <DurationSection
          isExpanded={expandedSection === 'duration'}
          onChange={handleAccordionChange('duration')}
          selectedRange={selectedRange}
          onRangeChange={rangeChangeHandler}
          durationMetrics={durationMetrics}
          isMobile={isMobile}
        />

        <AIUsageSection
          isExpanded={expandedSection === 'aiUsage'}
          onChange={handleAccordionChange('aiUsage')}
          selectedRange={selectedRange}
          onRangeChange={rangeChangeHandler}
          aiUsageData={statisticsData.aiUsageData}
          isLoadingAIUsage={statisticsData.isLoadingAIUsage}
          isMobile={isMobile}
        />

        <MedicalActsSection
          isExpanded={expandedSection === 'medicalActs'}
          onChange={handleAccordionChange('medicalActs')}
          selectedRange={selectedRange}
          onRangeChange={rangeChangeHandler}
          establishmentId={establishmentId}
          isMobile={isMobile}
        />

        <EntityEvolutionSection
          isExpanded={expandedSection === 'entityEvolution'}
          onChange={handleAccordionChange('entityEvolution')}
          selectedRange={selectedRange}
          onRangeChange={rangeChangeHandler}
          entityEvolutionData={entityEvolutionData}
          isLoadingEntityEvolution={isLoadingEntityEvolution}
          isMobile={isMobile}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <DailySection
          isExpanded={expandedSection === 'daily'}
          onChange={handleAccordionChange('daily')}
          selectedDate={dayjs(selectedDate)}
          onDateChange={dateChangeHandler}
          dailyReasonsData={statisticsData.consultationStatistics.dailyReasons.data}
          dailyStatusData={statisticsData.consultationStatistics.dailyStatus.data}
          dailyPatientsData={statisticsData.patientStatistics.dailyPatients.data}
          isMobile={isMobile}
        />

        <WeeklySection
          isExpanded={expandedSection === 'weekly'}
          onChange={handleAccordionChange('weekly')}
          selectedRange={selectedRange}
          onRangeChange={rangeChangeHandler}
          establishmentId={establishmentId}
          weeklyConsultationsData={statisticsData.consultationStatistics.weeklyConsultations.data}
          weeklySpecialtyData={statisticsData.patientStatistics.weeklySpecialty.data}
          weeklyConsultationsByTypeData={statisticsData.patientStatistics.weeklyConsultationsByType.data}
          isMobile={isMobile}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <MonthlyReportTab establishmentId={establishmentId} isSuperAdmin={isSuperAdmin} />
      </TabPanel>
    </Box>
  )
}
